import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, ArrowRightLeft, Unplug } from "lucide-react";
import { PlaceDialog } from "@/components/network/PlaceDialog";
import { HistoryDialog } from "@/components/network/HistoryDialog";
import { PortBar } from "@/components/network/StatusPill";
import { canManageWilayah } from "@/lib/network";

function ReleaseBox({ port, onDone, onCancel }) {
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/network/placements/${port.placement_id}/release`, { alasan });
      toast.success(`Port ${port.port} dikosongkan`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 flex gap-1.5" data-testid={`release-box-${port.port}`}>
      <Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="Alasan pelepasan" className="h-8 text-xs" data-testid="release-reason" />
      <Button size="sm" variant="destructive" disabled={busy || alasan.trim().length < 3} onClick={go} data-testid="release-confirm">Lepas</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Batal</Button>
    </div>
  );
}

function PortRow({ p, manage, onPlace, onMove, onReleased }) {
  const [rel, setRel] = useState(false);
  return (
    <div className={`rounded border p-2 text-xs ${p.customer_id ? "border-[#BFDBFE] bg-[#EFF6FF]" : "border-dashed border-[#E5E7EB]"}`}
         data-testid={`port-${p.port}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-mono-tab font-bold">Port {p.port}</span>
          {p.over_capacity && <span className="ml-1 text-[#DC2626]">(di atas kapasitas)</span>}
          {p.customer_id ? (
            <div className="truncate" data-testid={`port-${p.port}-customer`}>
              <b>{p.customer_id}</b> · {p.nama_customer || "-"}<div className="truncate text-[#6B7280]">{p.alamat || "-"}</div>
            </div>
          ) : <div className="text-[#6B7280]">Kosong</div>}
        </div>
        {manage && (p.customer_id ? (
          <div className="flex shrink-0 gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" title="Pindahkan" onClick={() => onMove(p)} data-testid={`port-${p.port}-move`}><ArrowRightLeft className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" title="Lepas" onClick={() => setRel((v) => !v)} data-testid={`port-${p.port}-release`}><Unplug className="h-3.5 w-3.5" /></Button>
          </div>
        ) : <Button size="sm" variant="outline" className="h-7" onClick={() => onPlace(p)} data-testid={`port-${p.port}-place`}>Isi</Button>)}
      </div>
      {rel && <ReleaseBox port={p} onCancel={() => setRel(false)} onDone={() => { setRel(false); onReleased(); }} />}
    </div>
  );
}

export function OdpPortsPanel({ odpId, me, onChanged, compact = false }) {
  const [odp, setOdp] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [hist, setHist] = useState(false);
  const load = useCallback(() => api.get(`/network/odp/${odpId}`).then((r) => setOdp(r.data)), [odpId]);
  useEffect(() => { load(); }, [load]);
  if (!odp) return <p className="text-xs text-[#6B7280]">Memuat port…</p>;
  const manage = canManageWilayah(me, odp.wilayah_id);
  const changed = () => { setDlg(null); load(); onChanged?.(); };
  return (
    <div className="space-y-2" data-testid="odp-ports-panel">
      <div className="flex items-center justify-between">
        <PortBar used={odp.port_terpakai} cap={odp.kapasitas_port} testid="odp-ports-bar" />
        <span className="text-xs text-[#6B7280]" data-testid="odp-ports-summary">{odp.port_terpakai} terisi · {odp.port_kosong} kosong</span>
        {me?.can_manage && manage && (
          <Button size="sm" variant="ghost" onClick={() => setHist(true)} data-testid="odp-history-btn"><History className="mr-1 h-3.5 w-3.5" />Riwayat</Button>
        )}
      </div>
      {odp.status !== "AKTIF" && <p className="text-xs text-[#B45309]">ODP tidak aktif — tidak menerima penempatan baru.</p>}
      <div className={`grid gap-1.5 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        {odp.ports.map((p) => (
          <PortRow key={p.port} p={p} manage={manage && (p.customer_id || odp.status === "AKTIF")} onReleased={changed}
                   onPlace={(x) => setDlg({ mode: "place", port: x.port })}
                   onMove={(x) => setDlg({ mode: "move", port: x.port, customer: { customer_id: x.customer_id } })} />
        ))}
      </div>
      {dlg && <PlaceDialog open mode={dlg.mode} odp={odp} port={dlg.port} customer={dlg.customer} me={me}
                           onClose={() => setDlg(null)} onDone={changed} />}
      {hist && <HistoryDialog odp={odp} onClose={() => setHist(false)} />}
    </div>
  );
}
